import { useState } from "react";
import { Tag as TagIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { TagList, TagFormDialog, TagDeleteDialog } from "@/components/ui/Tag";
import { SettingsPageHeader, SettingsPanel } from "./SettingsSection";
import {
  useDocumentTags,
  useTagUsageCounts,
  useCreateDocumentTag,
  useUpdateDocumentTag,
  useDeleteDocumentTag,
} from "@/hooks/use-documents";
import type { Tag } from "@/components/ui/Tag/types";
import type { Id } from "@/packages/types";

export function SettingsTagsSection() {
  const { t } = useTranslation(["settings"]);
  const { tags, isLoading: isTagsLoading } = useDocumentTags();
  const { usageMap, isLoading: isUsageLoading } = useTagUsageCounts();
  const createTag = useCreateDocumentTag();
  const updateTag = useUpdateDocumentTag();
  const deleteTag = useDeleteDocumentTag();
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null);

  const isLoading = isTagsLoading || isUsageLoading;

  const handleCreate = () => {
    setEditingTag(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setFormDialogOpen(true);
  };

  const handleDelete = (tag: Tag) => {
    setDeletingTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: { name: string; color: string }) => {
    if (editingTag) {
      await updateTag({ id: editingTag.id as Id<"documentTags">, ...data });
    } else {
      await createTag(data);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deletingTag) {
      await deleteTag({ id: deletingTag.id as Id<"documentTags"> });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-muted">{t("settings:tags.loading")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("settings:sections.organization")}
        title={t("settings:tags.title")}
        description={t("settings:tags.description")}
        action={
          <Button variant="primary" icon={Plus} onClick={handleCreate}>
            {t("settings:tags.addTag")}
          </Button>
        }
      />
      <SettingsPanel>
        {tags.length > 0 ? (
          <TagList tags={tags} onEdit={handleEdit} onDelete={handleDelete} />
        ) : (
          <div className="flex flex-col items-center justify-center h-48 rounded-xl border border-dashed border-border">
            <TagIcon className="h-8 w-8 text-muted mb-3" />
            <p className="text-sm text-muted">{t("settings:tags.empty")}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={handleCreate}
            >
              {t("settings:tags.createFirst")}
            </Button>
          </div>
        )}

        <TagFormDialog
          open={formDialogOpen}
          onClose={() => setFormDialogOpen(false)}
          mode={editingTag ? "edit" : "create"}
          initialData={editingTag ?? undefined}
          onSubmit={handleFormSubmit}
        />

        <TagDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          tagName={deletingTag?.name ?? ""}
          usageCount={deletingTag ? usageMap.get(deletingTag.id) : undefined}
          onConfirm={handleDeleteConfirm}
        />
      </SettingsPanel>
    </div>
  );
}
